// Smart Route V2 (SmartRouteV2Node) renders one routing output connection per
// user-defined route. Unlike fixed-route nodes (ConditionalV2Node), its handles
// are derived from config at runtime: the backend node sets per-instance
// `output_paths` from each route's stable `id`, and the canvas derives the same
// handles (with the route `name` as label) directly from `nodeConfig.routes`.

export const SMART_ROUTE_NODE_TYPES = new Set<string>(["SmartRouteV2Node"]);

export function isSmartRouteNode(nodeType: string | undefined): boolean {
  return nodeType !== undefined && SMART_ROUTE_NODE_TYPES.has(nodeType);
}

export interface RouteHandle {
  id: string;
  label: string;
}

/** Derive the routing handles ({id, label}) from a Smart Route node's config.
 * Handle id = the stable `RouteRow.id` (what edges bind to); label = the route
 * `name` (display only). When a row has no name (e.g. a legacy node), fall back
 * to a positional "Route N" rather than the raw UUID id. Rows without an id are
 * skipped (no handle yet). */
export function routeHandlesFromConfig(
  nodeConfig: Record<string, unknown> | undefined
): RouteHandle[] {
  const routes = nodeConfig?.routes;
  if (!Array.isArray(routes)) return [];
  const out: RouteHandle[] = [];
  for (const row of routes) {
    if (!row || typeof row !== "object") continue;
    const id = (row as { id?: unknown }).id;
    if (typeof id !== "string" || !id) continue;
    const name = (row as { name?: unknown }).name;
    const label =
      typeof name === "string" && name.trim()
        ? name
        : `Route ${out.length + 1}`;
    out.push({ id, label });
  }
  return out;
}

// Switch V2 (SwitchV2Node) mirrors Smart Route's per-handle routing, but its
// handles come from `nodeConfig.branches` (condition groups) plus a fixed
// `default` handle that fires when no branch matches.
export const SWITCH_NODE_TYPES = new Set<string>(["SwitchV2Node"]);
export const SWITCH_DEFAULT_HANDLE = "default";

export function isSwitchNode(nodeType: string | undefined): boolean {
  return nodeType !== undefined && SWITCH_NODE_TYPES.has(nodeType);
}

/** Derive the switch handles ({id, label}) from a Switch node's config: one per
 * branch (id = stable `SwitchBranch.id`, label = branch `name`) followed by the
 * fixed `default` handle. */
export function switchHandlesFromConfig(
  nodeConfig: Record<string, unknown> | undefined
): RouteHandle[] {
  const out: RouteHandle[] = [];
  const branches = nodeConfig?.branches;
  if (Array.isArray(branches)) {
    for (const row of branches) {
      if (!row || typeof row !== "object") continue;
      const id = (row as { id?: unknown }).id;
      if (typeof id !== "string" || !id) continue;
      const name = (row as { name?: unknown }).name;
      out.push({ id, label: typeof name === "string" && name ? name : id });
    }
  }
  out.push({ id: SWITCH_DEFAULT_HANDLE, label: "Default" });
  return out;
}

// Merge Paths (MergePathsNode) mirrors the same per-instance derivation on the
// INPUT side: one target handle per `nodeConfig.paths` row (keyed by the stable
// `MergePathRow.id`). Legacy nodes without rows keep the fixed path_a/path_b
// handles their saved edges point at.
export const MERGE_PATHS_NODE_TYPES = new Set<string>(["MergePathsNode"]);

export function isMergePathsNode(nodeType: string | undefined): boolean {
  return nodeType !== undefined && MERGE_PATHS_NODE_TYPES.has(nodeType);
}

export const MERGE_PATHS_LEGACY_HANDLES: RouteHandle[] = [
  { id: "path_a", label: "Path A" },
  { id: "path_b", label: "Path B" },
];

/** Derive the input handles ({id, label}) from a Merge Paths node's config:
 * one per `paths` row (id = stable `MergePathRow.id`, label = positional
 * "Path N"). Rows without an id are skipped (no handle yet); a config with no
 * usable rows falls back to the legacy fixed handles. */
export function mergeInputHandlesFromConfig(
  nodeConfig: Record<string, unknown> | undefined
): RouteHandle[] {
  const rows = nodeConfig?.paths;
  const out: RouteHandle[] = [];
  if (Array.isArray(rows)) {
    for (const row of rows) {
      if (!row || typeof row !== "object") continue;
      const id = (row as { id?: unknown }).id;
      if (typeof id !== "string" || !id) continue;
      out.push({ id, label: `Path ${out.length + 1}` });
    }
  }
  return out.length > 0 ? out : MERGE_PATHS_LEGACY_HANDLES;
}

/** A node whose output handles are derived from config at runtime (Smart Route
 * or Switch). */
export function isDynamicHandleNode(nodeType: string | undefined): boolean {
  return isSmartRouteNode(nodeType) || isSwitchNode(nodeType);
}

/** Unified handle resolver for dynamic-handle nodes. */
export function dynamicHandlesFromConfig(
  nodeType: string | undefined,
  nodeConfig: Record<string, unknown> | undefined
): RouteHandle[] {
  if (isSwitchNode(nodeType)) return switchHandlesFromConfig(nodeConfig);
  if (isSmartRouteNode(nodeType)) return routeHandlesFromConfig(nodeConfig);
  return [];
}
